<template>
  <div>
    <StatsTimeRangePicker
      v-if="props.showFilters"
      v-model:start="start"
      v-model:end="end"
    />
    <div
      v-if="props.showFilters"
      class="@container grid grid-cols-4 gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg p-2 my-2"
    >
      <div v-if="!props.domain" class="relative col-span-4 @2xl:col-span-2">
        <MagnifyingGlass
          class="pointer-events-none w-6 h-6 absolute top-7 left-1 m-1 fill-gray-300"
        />
        <label for="domain-name"> Domain </label>
        <input
          type="text"
          aria-label="Domain"
          placeholder="example.com"
          v-model="searchNameFilter"
          class="w-full h-10 pl-10 transition-colors bg-white"
          @change="fetchActivity(true)"
        />
      </div>
      <div class="col-span-4 @sm:col-span-2 @2xl:col-span-1">
        <label for="sortby"> Status </label>
        <select
          name="status"
          aria-label="Status"
          class="w-full h-10 bg-white"
          v-model="statusFilter"
          @change="fetchActivity(true)"
        >
          <option value="">Any</option>
          <option value="approved">Approved</option>
          <option value="denied">Denied</option>
        </select>
      </div>
      <div class="col-span-4 @sm:col-span-2 @2xl:col-span-1">
        <label for="sortby"> Suggestion Type </label>
        <select
          name="suggestionType"
          aria-label="suggestion type"
          class="w-full h-10 bg-white"
          v-model="overrideTypeFilter"
          @change="fetchActivity(true)"
        >
          <option value="">Any</option>
          <option value="agent_about_me">About Me</option>
          <option value="office_mission">Mission Statement</option>
          <option value="team_member">Team Member Description</option>
        </select>
      </div>
      <div class="relative col-span-4 @2xl:col-span-2">
        <MagnifyingGlass
          class="pointer-events-none w-6 h-6 absolute top-7 left-1 m-1 fill-gray-300"
        />
        <label for="domain-name"> Reasoning </label>
        <input
          type="text"
          aria-label="Domain"
          placeholder="preference"
          v-model="searchReasoningFilter"
          class="w-full h-10 pl-10 transition-colors bg-white"
          @change="fetchActivity(true)"
        />
      </div>
      <div class="hidden @sm:col-span-2 @sm:block @2xl:col-span-1"></div>
      <div class="col-span-4 @sm:col-span-2 @2xl:col-span-1 flex self-end pt-2">
        <a
          class="text-center block w-full p-2 rounded-md text-white bg-gray-600 hover:bg-gray-800 hover:no-underline"
          download="activity.csv"
          :href="csvHref"
        >
          Download as CSV
        </a>
      </div>
    </div>
    <div class="@container border-2 border-gray-200 rounded-lg overflow-auto">
      <div class="bg-gray-100 px-5 py-2 grid grid-cols-4 font-semibold">
        <div>Activity</div>
        <div class="hidden @2xl:block">Date</div>
        <div class="hidden @2xl:block">Detail</div>
        <div class="hidden @2xl:block">Agent</div>
      </div>
      <ol v-if="activity.length" class="w-full space-y-2">
        <li
          v-for="item in activity"
          class="grid @xl:grid-cols-3 @2xl:grid-cols-4 gap-4 justify-between items-center border-b-2 last:border-b-0 border-gray-200 pt-3 pb-4 px-5 mb-0 hover:bg-gray-50 cursor-pointer transition-colors"
          @click="navigateToItem(item)"
        >
          <div>
            <div class="font-semibold flex gap-1 items-center">
              <component class="w-4 h-4" :is="getIcon(item)"></component
              >{{ prettySuggestionType(item.key) }}
            </div>
            <div class="truncate">{{ item.domain }}</div>

            <div
              class="mt-1 flex items-start gap-1 text-sm @2xl:text-xs flex-wrap"
            >
              <div
                class="w-fit px-2 py-1 rounded font-semibold"
                :class="{
                  'bg-green-200 text-green-900':
                    item.status === 'approved' || item.status === 'retired',
                  'bg-red-200 text-red-900': item.status === 'denied',
                }"
              >
                {{ getStatus(item.status) }}
              </div>
              <div
                v-if="item.modified_by_reviewer"
                class="w-fit px-2 py-1 rounded font-semibold bg-gray-200 text-gray-600"
              >
                edited by <span>Agent/ATM</span>
              </div>
              <div
                v-if="item.status === 'retired'"
                class="w-fit px-2 py-1 rounded font-semibold bg-gray-200 text-gray-600"
              >
                retired
              </div>
            </div>
          </div>
          <div
            class="flex flex-row items-baseline gap-1.5 @2xl:flex-col @2xl:gap-0 bg-gray-100 @2xl:bg-transparent rounded-lg px-3 py-1 @2xl:p-0 w-fit"
          >
            <div class="flex gap-1.5 items-center">
              <Clock class="@2xl:hidden w-4 h-4" />
              <div
                class="flex flex-wrap items-baseline gap-1.5 @xl:gap-0 @xl:flex-col"
              >
                <span class="font-medium text-nowrap">{{
                  formatDate(item.date_reviewed)
                }}</span>

                <span class="text-sm @2xl:ml-0 @2xl:block text-nowrap">{{
                  formatTime(item.date_reviewed)
                }}</span>
              </div>
            </div>
          </div>
          <div v-if="item.status_detail || item.additional_feedback">
            <div>
              <span class="@2xl:hidden">Reason: </span>{{ item.status_detail }}
            </div>
            <div
              v-if="item.additional_feedback"
              class="mt-2 p-1 rounded-lg bg-gray-50 border-2 border-gray-200 text-xs"
            >
              <div
                class="min-w-32 bg-gray-200 p-1 rounded text-gray-900 font-semibold"
              >
                Additional feedback from the agent/ATM:
              </div>
              <div class="p-1 max-h-24 overflow-scroll">
                {{ item.additional_feedback }}
              </div>
            </div>
          </div>
          <div v-else class="hidden @2xl:block">---</div>
          <div>
            <div class="font-semibold">
              {{ item.agent_name }} ({{ item.agent_alias }})
            </div>
            <div class="text-xs">
              Associate ID: {{ item.agent_associate_id }}
            </div>
          </div>
        </li>
      </ol>
      <div
        v-else-if="loadingMore"
        class="h-40 flex items-center justify-center"
      >
        <LoadingSpinner />
      </div>
      <div v-else class="text-gray-500 text-center px-3 py-2">
        No results found
      </div>
    </div>
    <button
      v-if="activity.length && props.showMore && nextUrl"
      class="w-full mt-2"
      @click="fetchActivity(false)"
    >
      <LoadingSpinner v-if="loadingMore" gray small />
      <span v-else> Show More </span>
    </button>
  </div>
</template>

<script setup>
import { parseISO } from "date-fns";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import Bullseye from "@/components/icons/Bullseye.vue";
import Clock from "@/components/icons/Clock.vue";
import MagnifyingGlass from "@/components/icons/MagnifyingGlass.vue";
import UserPen from "@/components/icons/UserPen.vue";
import UserPlus from "@/components/icons/UserPlus.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import StatsTimeRangePicker from "@/components/StatsTimeRangePicker.vue";
import useUserStore from "@/stores/user";

const userStore = useUserStore();
const { vendorKey } = storeToRefs(userStore);

const router = useRouter();

const props = defineProps({
  domain: { type: String, required: false },
  showFilters: { type: Boolean, default: false },
  showMore: { type: Boolean, default: false },
});

const activity = ref([]);
const nextUrl = ref(null);
const loadingMore = ref(false);

const searchNameFilter = ref("");
const statusFilter = ref("");
const overrideTypeFilter = ref("");
const searchReasoningFilter = ref("");
const start = ref("");
const end = ref("");

function navigateToItem(item) {
  let name = "Domain Overview";
  switch (item.key) {
    case "office.team.member_edits":
      name = "Team - Members";
      break;
    case "agent.about_me":
      name = "About Me";
      break;
    case "office.mission":
      name = "Mission";
      break;
  }
  router.push({ name: name, params: { domain: item.domain } });
}

function getIcon(item) {
  switch (item.key) {
    case "office.team.member_edits":
      return UserPen;
    case "agent.about_me":
      return UserPlus;
    case "office.mission":
      return Bullseye;
  }
}

const formatDate = function formatDate(isoDate) {
  return parseISO(isoDate).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatTime = function formatDate(isoDate) {
  return parseISO(isoDate).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

function prettySuggestionType(key) {
  return {
    "office.team.member_edits": "TM Description",
    "agent.about_me": "About Me",
    "office.mission": "Mission Statement",
  }[key];
}

function getStatus(status) {
  if (status === "retired") {
    return "approved";
  }
  return status;
}

function getSearchParams(format = "json") {
  const params = new URLSearchParams({
    search_name: props.domain ? props.domain : searchNameFilter.value,
    status: statusFilter.value,
    override_type: overrideTypeFilter.value,
    search_reasoning: searchReasoningFilter.value,
    start: start.value,
    end: end.value,
    format,
  });
  return params.toString();
}

async function fetchActivity(reset = false) {
  loadingMore.value = true;

  if (reset) {
    nextUrl.value = null;
    activity.value = [];
  }

  let url;
  if (nextUrl.value) {
    url = nextUrl.value;
  } else {
    url = `/api/cmp/${vendorKey.value}/domains/override_activity/?${getSearchParams()}`;
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Failed to retrieve recent activity");
  }
  const paginatedResponse = await response.json();

  activity.value = [...activity.value, ...paginatedResponse.results];
  nextUrl.value = paginatedResponse.next;
  loadingMore.value = false;
}

const csvHref = computed(() => {
  return `/api/cmp/${vendorKey.value}/domains/override_activity/?${getSearchParams("csv")}`;
});

watch([start, end], async ([_newStart, _newEnd], [_oldStart, _oldEnd]) => {
  fetchActivity(true);
});

onMounted(() => {
  if (!props.showFilters) {
    fetchActivity(true);
  }
});
</script>
